// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import setSelectedImageIndex from "@insite/client-framework/Store/Pages/ProductDetails/Handlers/SetSelectedImageIndex";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ProductImageCarouselStyles } from "@insite/content-library/Components/ProductImageCarousel";
import { ButtonProps } from "@insite/mobius/Button";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import { LazyImageProps } from "@insite/mobius/LazyImage";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import setSelectedVideoIndex from "@steiner/App/Handlers/ProductDetails/SetSelectedVideoIndex";
import { CustomAppState } from "@steiner/App/Store/Reducers";
import { getProductVideos, getSelectedVideoIndex } from "@steiner/App/Store/Selectors/ProductDetailsSelector";
import ProductImageCarousel from "@steiner/Overrides/Components/ProductImageCarousel";
import React from "react";
import { connect, ResolveThunks } from "react-redux";

const enum fields {
    scrollingBehavior = "scrollingBehavior",
    numberOfVisibleThumbnails = "numberOfVisibleThumbnails",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.scrollingBehavior]: string;
        [fields.numberOfVisibleThumbnails]: number;
    };
}

// XNG-Change
// parse ownProps to the mapStateToProps and use the CustomAppState
const mapStateToProps = (state: CustomAppState, ownProps: HasProduct) => ({
    selectedImageIndex: state.pages.productDetails.selectedImageIndex,
    // XNG-Change
    selectedVideoIndex: getSelectedVideoIndex(state, ownProps.product),
    videos: getProductVideos(state, ownProps.product),
});

const mapDispatchToProps = {
    setSelectedImageIndex,
    // XNG-Change
    setSelectedVideoIndex,
};

type Props = OwnProps & HasProduct & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface ProductDetailsImageCarouselStyles {
    imageCarousel?: ProductImageCarouselStyles;

    /**
     * @deprecated Use imageCarousel styles instead.
     */
    mainContainer?: GridContainerProps;
    prevArrowGridItem?: GridItemProps;
    prevArrowButton?: ButtonProps;
    carouselGridItem?: GridItemProps;
    carouselWrapper?: InjectableCss;
    carouselSlidesContainer?: InjectableCss;
    carouselSlide?: InjectableCss;
    carouselSlideInner?: InjectableCss;
    image?: LazyImageProps;
    selectedImage?: LazyImageProps;
    nextArrowGridItem?: GridItemProps;
    nextArrowButton?: ButtonProps;
}

export const productDetailsImageCarouselStyles: ProductDetailsImageCarouselStyles = {};

const styles = productDetailsImageCarouselStyles;

const ProductDetailsImageCarousel = ({
    product,
    selectedImageIndex,
    setSelectedImageIndex,
    fields: { scrollingBehavior, numberOfVisibleThumbnails },
    // XNG-Change
    selectedVideoIndex,
    videos,
    setSelectedVideoIndex,
}: Props) => {
    const selectImageHandler = (index: number) => {
        setSelectedImageIndex({ index });
        // XNG-Change
        if (videos && videos.length > 0) {
            setSelectedVideoIndex({ index: -1, product });
        }
    };

    // XNG-Change
    const selectVideoHandler = (index: number) => {
        setSelectedImageIndex({ index: -1 });
        setSelectedVideoIndex({ index, product });
    };

    if (!product || !product.images || product.images.length <= 1) {
        return null;
    }

    return (
        <ProductImageCarousel
            scrollingBehavior={scrollingBehavior}
            numberOfVisibleThumbnails={numberOfVisibleThumbnails}
            selectedIndex={selectedImageIndex}
            onSelectImage={selectImageHandler}
            extendedStyles={styles.imageCarousel}
            // XNG-Change
            selectedVideoIndex={selectedVideoIndex}
            onSelectVideo={selectVideoHandler}
        />
    );
};

const widgetModule: WidgetModule = {
    component: withProduct(connect(mapStateToProps, mapDispatchToProps)(ProductDetailsImageCarousel)),
    definition: {
        displayName: "Image Carousel",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
        fieldDefinitions: [
            {
                name: fields.scrollingBehavior,
                displayName: "Scrolling Behavior",
                editorTemplate: "RadioButtonsField",
                defaultValue: "horizontal",
                horizontal: true,
                fieldType: "General",
                options: [
                    {
                        displayName: "Horizontal",
                        value: "horizontal",
                    },
                    {
                        displayName: "Vertical",
                        value: "vertical",
                    },
                ],
                sortOrder: 0,
            },
            {
                name: fields.numberOfVisibleThumbnails,
                displayName: "Number Of Visible Thumbnails",
                editorTemplate: "IntegerField",
                min: 1,
                max: 20,
                defaultValue: 6,
                fieldType: "General",
                sortOrder: 1,
            },
        ],
    },
};

export default widgetModule;
